import * as React from 'react';
import { resolve } from 'inversify-react';
import { observable, computed } from 'mobx';
import { observer } from 'mobx-react';

import { ToastNotifications } from 'src/core/ui/toast';
import { ControllerProps } from 'src/router';
import { ObjectUtils } from 'src/core/utils/object';
import { ConfigurationService } from 'src/core/services/configuration.service';
import { UserFormView } from './user-form.view';
import { UserDataService, UserItemModel } from './user-form.data';
import { IdentityService } from 'src/core/services/identity.service';
import { UserRoleDataService } from 'src/app/general/roles/parts/role-user-editor.data';
import { ContextService } from '../../../../core/services/context.service';

interface Props extends ControllerProps<{ id: string; }> {
}

interface State {
	defaultLanguage: string;
	mode: ControlMode;
	refresh: boolean;
}

@observer
export class UserFormController extends React.Component<Props, State> {
	@observable
	dataService: UserDataService;

	@resolve
	contextService: ContextService;

	@resolve
	identityService: IdentityService;

	@resolve
	toast: ToastNotifications;

	@resolve
	configurationService: ConfigurationService;

	@observable
	userRoleService: UserRoleDataService;

	constructor(props: Readonly<Props>) {
		super(props);

		this.state = {
			refresh: false,
			mode: 'display',
			defaultLanguage: 'en'
		};
	}

	componentDidMount() {
		this.dataService = UserDataService.make(this.contextService);
		this.userRoleService = UserRoleDataService.make(this.contextService);

		if (!this.isAdministrator() && this.identityService.userId.toString() !== this.props.match.params.id) {
			this.props.history.push('/login', { from: this.props.location, requirePrivileges: true });
			return;
		}

		if (this.props.match.params.id !== 'new')
			this.loadUserRoles(Number.parseInt(this.props.match.params.id));

		this.props.setBusy(true);
		this.configurationService.current()
			.then((appConfig) => {
				this.setState({ defaultLanguage: appConfig.defaultLanguage });
				if (this.props.match.params.id == 'new') {
					// Create new
					this.createUser()
						.then(() => {
							this.handleModeChange('edit');
							this.props.setBusy(false);
						})
						.catch((error) => {
							this.props.setBusy(false);
							this.toast.showToast('Request failed', ObjectUtils.parseError(error), 'error');
						});
				} else {
					// Load entity
					this.dataService.addFilter('id', 'eq', this.props.match.params.id);
					this.dataService.addSorting('id', 'asc', true);
					this.dataService.pageSize = 1;
					this.dataService.load(query => query)
						.catch((error) => {
							this.toast.showToast('Request failed', ObjectUtils.parseError(error), 'error');
						})
						.then(() => {
							this.props.setBusy(false);
						});
				}
			});
	}

	private loadUserRoles(userId: number): void {
		this.props.setBusy(true);
		this.userRoleService.addFilter('userId', 'eq', userId);
		this.userRoleService.addSorting('roleId', 'asc', true);
		this.userRoleService.load(query => query)
			.catch((error) => {
				this.toast.showToast('Request failed', ObjectUtils.parseError(error), 'error');
			})
			.then(() => {
				this.props.setBusy(false);
			});
	}

	private isAdministrator(): boolean {
		return this.identityService.isAdmin;
	}

	private backUrl(): string {
		return this.isAdministrator() ? '/general/users' : '/';
	}

	private createUser(): Promise<UserItem> {
		const user = this.dataService.add(this.state.defaultLanguage);

		return user;
	}

	@computed
	protected get title(): string {
		const isNew = this.props.match.params.id == 'new';
		if (isNew) {
			return 'New User';
		} else {
			return `${this.dataService.item.displayName}`;
		}
	}

	handleModeChange = (mode: ControlMode): void => {
		this.setState({ mode });
	}

	createNewUserRole = (roleId: number): Promise<UserRoleSummary> => {
		return this.userRoleService.add(this.dataService.item.id, roleId);
	};

	cleanEmptyRoleAssignment = (): Promise<void> => {
		this.userRoleService.cleanEmptyAssignments();

		return Promise.resolve();
	};

	handleAvatarChange = (ev: React.ChangeEvent<HTMLInputElement>): void => {
		const file: File = ev.target.files[0];
		if (file) {
			const contentReader = new FileReader();
			if (file.size > 500 * 500) {
				this.toast.showToast('Image upload error!', 'Image too large', 'error');
				return;
			}

			contentReader.onload = (e: Event) => {
				const result: ArrayBuffer = (e.target as any).result;

				this.dataService.item.avatar = new Uint8Array(result);
				this.setState({
					refresh: !this.state.refresh
				});
			};
			contentReader.readAsArrayBuffer(file);
		}
	};

	save = async (): Promise<void> => {
		const item = this.dataService.item as UserItemModel;
		let errors = await item.validate(true);

		const passwordErrors = await item.validatePasswords(this.props.match.params.id == 'new' ? 'new' : 'update');

		if (this.userRoleService.items.length == 0) {
			errors.push({
				property: 'roles',
				text: 'The user must have a role'
			});
		}

		errors = errors.concat(passwordErrors);

		if (errors.length == 0) {
			this.props.setBusy(true);

			this.dataService.save()
				.then(() => {
					this.handleModeChange('display');
					this.toast.showDataSaved();
					this.props.history.replace(this.backUrl());
					this.userRoleService.items.forEach(assignmet => {
						assignmet.userId = item.id;
					});

					this.userRoleService.save().then(() => {
						this.props.setBusy(false);
						if (this.identityService.userId == item.id) {
							this.identityService.refreshTokenAction();
						}
					})
						.catch((error) => {
							this.props.setBusy(false);
							this.toast.showToast('Request failed', ObjectUtils.parseError(error), 'error');
						});

				})
				.catch((error) => {
					this.props.setBusy(false);
					this.toast.showToast('Request failed', ObjectUtils.parseError(error), 'error');
				});
		} else {
			this.toast.showToast('Validation error!', errors.map(e => e.text), 'error');
		}
	};

	cancel = (): void => {
		this.props.history.push(this.backUrl());
	};

	render(): JSX.Element {
		if (this.dataService == null || this.dataService.item == null) {
			return null;
		}

		return (
			<UserFormView
				title={this.title}
				mode={this.state.mode}
				isAdministrator={this.isAdministrator()}
				operation={this.props.match.params.id == 'new' ? 'new' : 'update'}
				onModeChanged={this.handleModeChange}
				cancel={this.cancel}
				save={this.save}
				roles={this.userRoleService}
				model={this.dataService.item}
				setBusy={this.props.setBusy}
				onAvatarChange={this.handleAvatarChange}
				onCancel={this.cleanEmptyRoleAssignment}
				createNewUserRole={this.createNewUserRole}
				contextService={this.contextService}
			/>
		);
	}
}



// WEBPACK FOOTER //
// ./src/app/general/users/form/user-form.controller.tsx